import { useEffect, useState, useCallback } from 'react'
import { api } from '../lib/api'
import { useToast } from '../components/ui/Toast'
import { Button } from '../components/ui/Button'
import { GlowCard } from '../components/ui/GlowCard'
import { StatCard } from '../components/ui/StatCard'
import { EmptyState } from '../components/ui/EmptyState'

interface NOCSummary {
  total_servers: number
  total_cameras: number
  total_offline: number
  storage_warnings: number
  sites: {
    connection_id: string; connection_name: string; client_name: string | null;
    camera_count: number; online_count: number; offline_count: number;
    max_storage_pct: number | null; status: string;
  }[]
}

interface ActiveAlerts {
  offline_cameras: { camera_id: string; camera_name: string; connection_name: string; went_offline: string; duration_min: number }[]
  storage_warnings: { server_name: string; drive_name: string; usage_percent: number; days_remaining: number | null }[]
}

function ServerIcon({ size = 20, style }: { size?: number; style?: React.CSSProperties }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={style}><rect x="2" y="2" width="20" height="8" rx="2" ry="2"/><rect x="2" y="14" width="20" height="8" rx="2" ry="2"/><line x1="6" y1="6" x2="6.01" y2="6"/><line x1="6" y1="18" x2="6.01" y2="18"/></svg>
}

function CameraIcon({ size = 20, style }: { size?: number; style?: React.CSSProperties }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={style}><path d="m16 16 3.553-2.276A1 1 0 0 0 20 12.87V7.97a1 1 0 0 0-.596-.868L16 4.97"/><rect x="2" y="4" width="14" height="12" rx="2"/></svg>
}

function AlertIcon({ size = 20, style }: { size?: number; style?: React.CSSProperties }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={style}><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>
}

function HardDriveIcon({ size = 20, style }: { size?: number; style?: React.CSSProperties }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={style}><line x1="22" y1="12" x2="2" y2="12"/><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/><line x1="6" y1="16" x2="6.01" y2="16"/><line x1="10" y1="16" x2="10.01" y2="16"/></svg>
}

export default function VMSHealth() {
  const [noc, setNoc] = useState<NOCSummary | null>(null)
  const [alerts, setAlerts] = useState<ActiveAlerts | null>(null)
  const [loading, setLoading] = useState(true)
  const { toast } = useToast()

  const load = useCallback(async () => {
    try {
      const [nocResp, alertResp] = await Promise.all([
        api.get<NOCSummary>('/integrations/dw/noc'),
        api.get<ActiveAlerts>('/integrations/dw/alerts'),
      ])
      setNoc(nocResp)
      setAlerts(alertResp)
    } catch { /* ignore */ }
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  if (loading) return <div style={{ padding: 24, color: 'var(--text-3)' }}>Loading VMS health...</div>

  if (!noc || noc.sites.length === 0) {
    return (
      <div style={{ padding: 24 }}>
        <EmptyState
          icon={CameraIcon}
          title="No VMS systems connected"
          description="Connect DW Spectrum servers in Settings → Integrations → DW Spectrum to monitor camera health."
        />
      </div>
    )
  }

  const hasAlerts = (alerts?.offline_cameras.length ?? 0) > 0 || (alerts?.storage_warnings.length ?? 0) > 0

  return (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-1)', margin: 0 }}>VMS Health</h1>
        <Button variant="secondary" onClick={() => { load(); toast('Refreshed', 'success') }}>Refresh</Button>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: 16 }}>
        <StatCard icon={ServerIcon} label="Servers" value={noc.total_servers} delay={0} />
        <StatCard icon={CameraIcon} label="Total Cameras" value={noc.total_cameras} delay={100} />
        <StatCard icon={AlertIcon} label="Offline" value={noc.total_offline} delay={200} />
        <StatCard icon={HardDriveIcon} label="Storage Warnings" value={noc.storage_warnings} delay={300} />
      </div>

      {/* Site Table */}
      <div style={{
        background: 'var(--bg-1)', borderRadius: 'var(--r-l)',
        border: '1px solid var(--border)', overflow: 'hidden',
      }}>
        <div style={{
          padding: '14px 20px', borderBottom: '1px solid var(--border)',
          fontSize: 14, fontWeight: 700, color: 'var(--text-1)',
        }}>
          Sites
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)' }}>
              <th style={thStyle}>Client / Server</th>
              <th style={thStyle}>Cameras</th>
              <th style={thStyle}>Offline</th>
              <th style={thStyle}>Storage</th>
              <th style={thStyle}>Status</th>
            </tr>
          </thead>
          <tbody>
            {noc.sites.map(site => {
              const hasIssue = site.offline_count > 0 || (site.max_storage_pct ?? 0) > 85
              return (
                <tr key={site.connection_id} style={{
                  borderBottom: '1px solid var(--border)',
                  background: hasIssue ? 'rgba(239,68,68,0.03)' : undefined,
                }}>
                  <td style={tdStyle}>
                    <span style={{ fontWeight: 500, color: 'var(--text-1)' }}>{site.connection_name}</span>
                    {site.client_name && <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{site.client_name}</div>}
                  </td>
                  <td style={{ ...tdStyle, fontFamily: 'var(--mono)' }}>
                    {site.online_count}/{site.camera_count}
                  </td>
                  <td style={tdStyle}>
                    {site.offline_count > 0 ? (
                      <span style={{ color: 'var(--err)', fontWeight: 600 }}>{site.offline_count}</span>
                    ) : (
                      <span style={{ color: 'var(--ok)' }}>0</span>
                    )}
                  </td>
                  <td style={tdStyle}>
                    {site.max_storage_pct != null ? (
                      <span style={{
                        fontFamily: 'var(--mono)',
                        color: site.max_storage_pct > 85 ? 'var(--err)' : 'var(--text-2)',
                        fontWeight: site.max_storage_pct > 85 ? 600 : 400,
                      }}>
                        {site.max_storage_pct.toFixed(0)}%
                      </span>
                    ) : '—'}
                  </td>
                  <td style={tdStyle}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12,
                    }}>
                      <span style={{
                        width: 7, height: 7, borderRadius: '50%',
                        background: site.status === 'connected' ? 'var(--ok)' : 'var(--err)',
                      }} />
                      {site.status === 'connected' ? 'Online' : site.status}
                    </span>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Active Alerts */}
      {hasAlerts && (
        <GlowCard style={{ padding: 20, borderColor: 'var(--err)' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--err)', marginBottom: 12 }}>
            Active Alerts
          </div>
          {alerts?.offline_cameras.map(cam => (
            <div key={cam.camera_id} style={{
              padding: '8px 12px', marginBottom: 8, borderRadius: 'var(--r-m)',
              background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)',
              fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <div>
                <span style={{ fontWeight: 500 }}>{cam.camera_name}</span>
                <span style={{ color: 'var(--text-3)', marginLeft: 8 }}>at {cam.connection_name}</span>
              </div>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--err)' }}>
                offline {Math.round(cam.duration_min)}m
              </span>
            </div>
          ))}
          {alerts?.storage_warnings.map((sw, i) => (
            <div key={i} style={{
              padding: '8px 12px', marginBottom: 8, borderRadius: 'var(--r-m)',
              background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.15)',
              fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <div>
                <span style={{ fontWeight: 500 }}>{sw.drive_name || 'Storage'}</span>
                <span style={{ color: 'var(--text-3)', marginLeft: 8 }}>on {sw.server_name}</span>
              </div>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--accent)' }}>
                {sw.usage_percent.toFixed(0)}%
                {sw.days_remaining != null && ` — ${sw.days_remaining.toFixed(0)} days left`}
              </span>
            </div>
          ))}
        </GlowCard>
      )}
    </div>
  )
}

const thStyle: React.CSSProperties = {
  textAlign: 'left', padding: '8px 16px', fontSize: 11,
  fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase',
  letterSpacing: '0.05em',
}
const tdStyle: React.CSSProperties = { padding: '10px 16px' }
